import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { useState } from "react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import {
  Button,
  DataTable,
  ErrorState,
  Field,
  Input,
  FilterBar,
  PageHeader,
  Select,
  StatusBadge,
  type Column,
  type Tone,
} from "../../shared/ui";
import type { EmployeeSummary, PageResponse } from "../../shared/types/api";
import { EmployeeFormDrawer } from "./EmployeeFormDrawer";
import { EmployeeDetailDrawer } from "./EmployeeDetailDrawer";

const STATUS_TONE: Record<string, Tone> = {
  ACTIVE: "green",
  PROBATION: "amber",
  NOTICE_PERIOD: "amber",
  SUSPENDED: "red",
  EXITED: "gray",
};

/**
 * The employee list.
 *
 * <p>Paged server-side rather than filtered in the browser. At thirty people the difference is
 * invisible; at the thousand this system is meant to reach, loading the table into memory to
 * filter it is the thing that makes the page slow.
 *
 * <p>Detail opens in a drawer rather than navigating away, so scanning a list and checking
 * individual records does not lose the reader's place.
 */
export function EmployeesPage() {
  const { can } = useAuth();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [query, setQuery] = useState("");
  const [department, setDepartment] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  const departments = useQuery({
    queryKey: ["departments"],
    queryFn: () => api.get<string[]>("/api/v1/employees/departments"),
  });

  const employees = useQuery({
    queryKey: ["employees", page, size, query, department],
    queryFn: () => {
      const search = new URLSearchParams({
        page: String(page),
        size: String(size),
        sort: "fullName,asc",
      });
      if (query) {
        search.set("q", query);
      }
      if (department) {
        search.set("department", department);
      }
      return api.get<PageResponse<EmployeeSummary>>(`/api/v1/employees?${search}`);
    },
    // Keeps the previous page on screen while the next loads, so paging does not flash empty.
    placeholderData: keepPreviousData,
  });

  const columns: Column<EmployeeSummary>[] = [
    {
      key: "fullName",
      header: "Name",
      render: (employee) => (
        <span className="font-medium text-ink-900">{employee.fullName}</span>
      ),
    },
    {
      key: "employeeCode",
      header: "Code",
      render: (employee) => (
        <span className="font-mono text-xs text-ink-500">{employee.employeeCode}</span>
      ),
    },
    { key: "officialEmail", header: "Email" },
    { key: "designation", header: "Designation" },
    { key: "department", header: "Department" },
    {
      key: "reportingManagerName",
      header: "Manager",
      render: (employee) => employee.reportingManagerName ?? "—",
    },
    {
      key: "employmentStatus",
      header: "Status",
      render: (employee) => (
        <StatusBadge tone={STATUS_TONE[employee.employmentStatus] ?? "gray"}>
          {employee.employmentStatus.replace(/_/g, " ")}
        </StatusBadge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Employees"
        subtitle="People are never deleted. Someone who leaves is marked as exited so their history stays intact."
      />

      <FilterBar actions={
          can("USER_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              Add employee
            </Button>
          )
        }>
          <div className="w-72">
            <Field label="Search">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Name, email or code"
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setPage(0);
                  }}
                />
              </div>
            </Field>
          </div>

          <div className="w-56">
            <Field label="Department">
              <Select
                value={department}
                onChange={(event) => {
                  setDepartment(event.target.value);
                  setPage(0);
                }}
              >
                <option value="">All departments</option>
                {(departments.data ?? []).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
      </FilterBar>

      {employees.isError ? (
        <ErrorState error={employees.error} onRetry={() => void employees.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={employees.data?.content ?? []}
          rowKey={(employee) => employee.id}
          loading={employees.isPending}
          onRowClick={(employee) => setSelected(employee.id)}
          page={page}
          pageSize={size}
          totalElements={employees.data?.totalElements ?? 0}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          emptyTitle={query || department ? "Nothing matches those filters" : "No employees yet"}
          emptyMessage={
            query || department
              ? "Try a broader search, or clear the department filter."
              : "Add someone to send them an invitation to activate their account."
          }
        />
      )}

      <EmployeeFormDrawer
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => {
          void employees.refetch();
          void departments.refetch();
        }}
      />

      <EmployeeDetailDrawer
        employeeId={selected}
        onClose={() => setSelected(null)}
        onChanged={() => void employees.refetch()}
      />
    </>
  );
}
